import styled from "styled-components";
import Search from "shared/components/Search";
import useStudentAssignListView from "features/teacher/hooks/lesson/useStudentAssignListView";
import Modal from "shared/components/Modal";
import Refresh from "shared/components/Refresh";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface ListViewProps {
    clickedLesson: string;
}

const statusMeta = (status?: string) => {
    switch (status) {
        case "suspended":
            return { label: "휴학중", color: "#D97706", bg: "#FFFBEB" };
        case "graduated":
            return { label: "퇴원", color: "#64748B", bg: "#F1F5F9" };
        default:
            return { label: "재학중", color: hw.success, bg: "#F0FDF4" };
    }
};

const StudentAssignListView = ({ clickedLesson }: ListViewProps) => {
    const {
        search, searchedLists, onRefreshClick, onChange,
        onStudentClick, clickedStudentList,
        onAllStudentClick, onAssignStudentClick,
        isError, setIsError,
        isSucceed, setIsSucced,
        isAssignmentReady, isSaving, allChecked,
    } = useStudentAssignListView(clickedLesson);

    const cannotEdit = !isAssignmentReady || isSaving;

    return (
        <Container>
            <Modal isOpen={isError} onClose={() => setIsError(false)} title="ERROR" content="배정중 오류가 발생하였습니다" />
            <Modal isOpen={isSucceed} onClose={() => setIsSucced(false)} title="SUCCESS" content="배정에 성공하였습니다" />
            <Header>
                <div>
                    <Title>학생 목록</Title>
                    <Sub>배정할 원생을 선택한 뒤 배정하세요.</Sub>
                </div>
                <Refresh onRefreshClick={onRefreshClick} />
            </Header>
            <Search placeholder="학생 이름을 입력해 주세요" value={search} onChange={onChange} />
            <List>
                <ListHeader>
                    <CheckBox type="checkbox" onChange={onAllStudentClick} checked={allChecked} disabled={cannotEdit} aria-label="전체 선택" />
                    <Name>전체선택</Name>
                    {clickedStudentList.length > 0 ? <Count>{clickedStudentList.length}</Count> : null}
                </ListHeader>
                {searchedLists.length === 0 ? (
                    <Empty>
                        <EmptyTitle>{search ? "검색 결과가 없습니다" : "학생이 없습니다"}</EmptyTitle>
                        <EmptyText>
                            {search ? "다른 이름으로 다시 검색해 보세요." : "배정할 원생이 없습니다."}
                        </EmptyText>
                    </Empty>
                ) : (
                    searchedLists.map((item) => {
                        const status = statusMeta(item.status);
                        const on = clickedStudentList.includes(item.id);
                        return (
                            <Item key={item.id} $on={on}>
                                <CheckBox
                                    type="checkbox"
                                    onChange={() => onStudentClick(item.id)}
                                    checked={on}
                                    disabled={cannotEdit}
                                    aria-label={`${item.name} 선택`}
                                />
                                <Name>{item.name}</Name>
                                <StatusBadge $color={status.color} $bg={status.bg}>{status.label}</StatusBadge>
                            </Item>
                        );
                    })
                )}
            </List>
            <Buttons>
                <AssignButton type="button" onClick={onAssignStudentClick} disabled={cannotEdit}>
                    {isSaving ? "저장 중..." : "배정하기"}
                </AssignButton>
            </Buttons>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 18px 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const Title = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const Sub = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 280px;
    border: 1px solid ${hw.border};
    border-radius: 12px;
    overflow: auto;
    ${scrollbar}
`;

const ListHeader = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 10px 14px;
    background: #FAFBFC;
    border-bottom: 1px solid ${hw.border};
    position: sticky;
    top: 0;
    z-index: 1;
`;

const Item = styled.div<{ $on: boolean }>`
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 12px 14px;
    border-bottom: 1px solid ${hw.border};
    background: ${props => props.$on ? hw.brandSoft : hw.surface};

    &:hover {
        background: ${props => props.$on ? hw.brandSoft : hw.bg};
    }

    &:last-child {
        border-bottom: none;
    }
`;

const CheckBox = styled.input`
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
    accent-color: ${hw.brand};
    flex-shrink: 0;
`;

const Name = styled.span`
    flex: 1;
    min-width: 0;
    color: ${hw.text};
    font-size: 14px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Count = styled.span`
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: ${hw.brand};
    color: white;
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const StatusBadge = styled.span<{ $color: string; $bg: string }>`
    display: inline-flex;
    padding: 3px 8px;
    border-radius: 999px;
    background: ${props => props.$bg};
    color: ${props => props.$color};
    font-weight: 700;
    font-size: 11px;
    flex-shrink: 0;
`;

const Empty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 36px 16px;
    text-align: center;
`;

const EmptyTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${hw.text};
`;

const EmptyText = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.5;
`;

const Buttons = styled.div`
    display: flex;
    justify-content: flex-end;
    padding-top: 12px;
    border-top: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const AssignButton = styled.button`
    appearance: none;
    padding: 9px 16px;
    border: none;
    border-radius: 8px;
    background: ${hw.brand};
    color: white;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${hw.brandDark};
    }

    &:disabled {
        background: #CBD5E1;
        cursor: not-allowed;
    }
`;

export default StudentAssignListView;
